import { normalizeLocale } from './locales';

const EN_US = {
  hero: {
    title: 'Longevity is a practice, not a plan',
    body:
      'AI-guided daily goals for workouts, meals, recovery, and habits. Built from your profile, adapted when things change.',
    cta: 'Download Forza',
    phoneStageAria: 'Forza home screen preview',
    phoneImageAlt: 'Forza home screen',
  },
  why: {
    kicker: 'Why Forza',
    title: 'Your plan should survive your day',
    points: [
      {
        id: 'static-plan',
        lead: 'The plan was static.',
        body: "Your day shifted but your plan did not. One missed session becomes a lost week.",
      },
      {
        id: 'compounding-decisions',
        lead: 'Decisions compound.',
        body:
          'When motivation drops, the next action still has to be obvious, not buried in a tracker you stopped checking.',
      },
      {
        id: 'system-not-schedule',
        lead: 'You need a system, not a schedule.',
        body:
          'A system that meets you where you are every day, instead of hoping you show up.',
      },
    ],
    systems: [
      {
        id: 'daily-goals',
        icon: 'calendar',
        title: 'Daily goals, not vague intent',
        detail:
          'Every morning, concrete actions for workouts, meals, recovery, and habits, built from your profile.',
      },
      {
        id: 'adapts',
        icon: 'heart',
        title: 'Adapts when things change',
        detail:
          'Reschedule, swap, or ask the AI coach. Your plan stays useful instead of going stale.',
      },
      {
        id: 'verified-progress',
        icon: 'shield',
        title: 'Progress you can verify',
        detail:
          'Workout check-ins, meal photos, voice notes, and HealthKit. No more self-reporting on trust alone.',
      },
    ],
  },
  walkthrough: {
    kicker: 'How it works',
    title: 'See how a real day works.',
    previousStep: 'Previous step',
    nextStep: 'Next step',
    stepsAria: 'Walkthrough steps',
    goToStep: 'Go to step {step}',
  },
  features: {
    kicker: 'Core systems',
    title: 'Everything works in one loop.',
    body:
      'Planning, execution, recovery, and coaching in a single system so the next step is never a decision you have to make alone.',
    systems: [
      {
        id: 'daily-execution',
        icon: 'target',
        title: 'Daily execution',
        detail:
          'Daily goals with exercises, sets, reps, meals, and a built-in timer. Attention stays on what to do now.',
        active: true,
      },
      {
        id: 'recovery-planning',
        icon: 'heart',
        title: 'Recovery planning',
        detail:
          'Reschedule or swap when your day changes. The plan survives imperfect weeks.',
        active: true,
      },
      {
        id: 'progress-pressure',
        icon: 'flame',
        title: 'Progress pressure',
        detail:
          'Leaderboards, weekly duels, and streaks. Your effort is visible, not just to you.',
        active: true,
      },
      {
        id: 'ai-support',
        icon: 'mic',
        title: 'AI support',
        detail:
          'The coach knows your plan, your goals, and your constraints. Real answers, not motivational quotes.',
        active: true,
      },
      {
        id: 'workout-focus',
        icon: 'dumbbell',
        title: 'Workout focus',
        detail:
          'Timers and check-ins reduce friction during training sessions.',
        active: false,
      },
      {
        id: 'health-context',
        icon: 'clock',
        title: 'Health context',
        detail:
          'HealthKit can support steps, sleep, and activity verification.',
        active: false,
      },
    ],
  },
  waitlist: {
    title: 'Start using Forza today.',
    body: 'Download Forza from the App Store, create your account, and begin your longevity journey.',
    honeypotLabel: 'Company',
    fields: {
      email: 'Your email',
    },
    submit: 'Download on the App Store',
    submitting: 'Applying...',
    success:
      'Application received. If approved, you will receive an email with unlock instructions.',
    fallbackError: 'Unable to submit your request right now. Please try again.',
  },
  guideChapters: [
    {
      id: 'start',
      label: 'Start here',
      title: 'Orient yourself inside the product',
      description:
        'These screens explain what Forza is trying to do for you each day: show the current state, pull in supporting health context, and make the next action obvious.',
    },
    {
      id: 'execution',
      label: 'Daily execution',
      title: 'Move from intention to action',
      description:
        'This chapter covers the screens that help you complete the day itself: understanding a goal, checking in, and getting help when motivation or logistics start to slip.',
    },
    {
      id: 'progress',
      label: 'Tracking and motivation',
      title: 'See whether the system is working',
      description:
        'Forza tracks consistency across time, then adds optional competitive pressure when you need an extra reason to show up.',
    },
    {
      id: 'personal',
      label: 'Personalization and support',
      title: 'Keep the system aligned with real life',
      description:
        'The product remains useful only if you can keep it aligned with your constraints, preferences, notifications, and support needs as life changes.',
    },
  ],
  guideSlides: [
    {
      id: 'player-stats',
      chapterId: 'start',
      title: 'Your progress at a glance',
      description:
        'Player Stats gives you the fastest read on whether the week is stable or drifting. Levels, streaks, and key metrics give context before you decide what to do next.',
      imageSrc: '/about/player-stats.png',
    },
    {
      id: 'healthkit-toggle',
      chapterId: 'start',
      title: 'HealthKit integration',
      description:
        'Connect iPhone health data so eligible goals can be checked automatically. Steps, sleep, and activity become supporting signals rather than extra manual work.',
      imageSrc: '/about/healthkit-toggle.png',
    },
    {
      id: 'daily-quests',
      chapterId: 'start',
      title: 'Your daily goals',
      description:
        'Each day starts with a concrete set of goals shaped by your profile, preferences, and recovery state. The product is trying to remove ambiguity before the day begins.',
      imageSrc: '/about/daily-quests.png',
    },
    {
      id: 'quest-detail',
      chapterId: 'start',
      title: 'Goal details',
      description:
        'Each goal explains what to do, how to complete it, and what success looks like. This is where the plan stops being abstract and becomes executable.',
      imageSrc: '/about/quest-detail.png',
    },
    {
      id: 'meal-verify',
      chapterId: 'execution',
      title: 'Meal check-ins',
      description:
        'Meal check-ins connect planning to eating behavior without turning the experience into obsessive logging. The point is adherence feedback, not friction for its own sake.',
      imageSrc: '/about/meal-verify.png',
    },
    {
      id: 'workout-verify',
      chapterId: 'execution',
      title: 'Workout and mindfulness check-ins',
      description:
        'These check-ins make progress reflect completed effort. The system learns more when it knows what really happened instead of assuming the plan was followed.',
      imageSrc: '/about/workout-verify.png',
    },
    {
      id: 'timer',
      chapterId: 'execution',
      title: 'Workout timer',
      description:
        'The built-in timer reduces execution friction during training. Intervals, rest, and transitions are handled in one place so you can focus on moving.',
      imageSrc: '/about/workout-timer.png',
    },
    {
      id: 'open-mic-coach',
      chapterId: 'execution',
      title: 'Quick coaching',
      description:
        'Quick coaching is for fast questions, fast nudges, and moments where menu navigation is already too much friction. Ask, get the answer, keep going.',
      imageSrc: '/about/open-mic-coach.png',
    },
    {
      id: 'live-coach',
      chapterId: 'execution',
      title: 'Live coaching',
      description:
        'Live coaching is where Forza becomes most useful on difficult days. When energy drops, timing shifts, or tradeoffs become messy, the app can help you decide what happens next.',
      imageSrc: '/about/live-coach.png',
    },
    {
      id: 'tracker',
      chapterId: 'progress',
      title: 'Progress tracker',
      description:
        'The tracker shows patterns over time, not just isolated wins. Consistency only becomes visible when the product keeps a longer memory than the current day.',
      imageSrc: '/about/tracker.png',
    },
    {
      id: 'leaderboard',
      chapterId: 'progress',
      title: 'Leaderboard',
      description:
        'The leaderboard adds visible social pressure for users who respond well to competition. It is not the product core, but it can be a useful reinforcement layer.',
      imageSrc: '/about/leaderboard.png',
    },
    {
      id: 'duels',
      chapterId: 'progress',
      title: 'Duels',
      description:
        'Duels turn consistency into a head-to-head contest. They are most useful when a week needs extra stakes to keep your follow-through honest.',
      imageSrc: '/about/duels.png',
    },
    {
      id: 'settings-overview',
      chapterId: 'personal',
      title: 'Settings',
      description:
        'Settings centralize account, privacy, product controls, and support surfaces so the system remains understandable as it grows.',
      imageSrc: '/about/settings-overview.png',
    },
    {
      id: 'manage-plan',
      chapterId: 'personal',
      title: 'Manage your plan',
      description:
        'You can adjust training schedule, recovery needs, and broader plan settings without restarting your relationship with the product from scratch.',
      imageSrc: '/about/manage-plan.png',
    },
    {
      id: 'rescheduling-profile',
      chapterId: 'personal',
      title: 'Rescheduling and profile',
      description:
        'When a day changes, rescheduling helps the plan stay alive instead of becoming irrelevant. Profile updates keep the system aligned with evolving availability.',
      imageSrc: '/about/rescheduling-profile.png',
    },
    {
      id: 'training-preferences',
      chapterId: 'personal',
      title: 'Training preferences',
      description:
        'Equipment access, training taste, and practical constraints matter. Better preference data produces daily goals that feel realistic rather than aspirational.',
      imageSrc: '/about/training-preferences.png',
    },
    {
      id: 'notifications-guide',
      chapterId: 'personal',
      title: 'Notifications and feature guide',
      description:
        'Notifications keep the loop active during the day, while the feature guide gives users a place to re-learn the product when something feels unclear.',
      imageSrc: '/about/notifications-guide.png',
    },
    {
      id: 'support',
      chapterId: 'personal',
      title: 'Support',
      description:
        'Early-stage products stay trustworthy only if support is easy to reach. The support flow keeps real help inside the product rather than forcing users to hunt for it.',
      imageSrc: '/about/support.png',
    },
  ],
};

const IT_IT = {
  hero: {
    title: 'La longevità è una pratica, non un piano',
    body:
      'Obiettivi quotidiani guidati dall\'AI per workout, meal, recovery e abitudini. Costruiti sul tuo profilo, adattati quando la giornata cambia.',
    cta: 'Scarica Forza',
    phoneStageAria: 'Anteprima schermata home di Forza',
    phoneImageAlt: 'Schermata home di Forza',
  },
  why: {
    kicker: 'Perché Forza',
    title: 'Il tuo piano deve sopravvivere alla tua giornata',
    points: [
      {
        id: 'static-plan',
        lead: 'Il piano era statico.',
        body: 'La tua giornata cambiava ma il piano no. Una sessione saltata diventava una settimana persa.',
      },
      {
        id: 'compounding-decisions',
        lead: 'Le decisioni si accumulano.',
        body:
          'Quando la motivazione cala, la prossima azione deve restare ovvia, non sepolta in un tracker che hai smesso di guardare.',
      },
      {
        id: 'system-not-schedule',
        lead: 'Ti serve un sistema, non un calendario.',
        body:
          'Un sistema che ti incontra ogni giorno dove sei davvero, invece di sperare semplicemente che tu ti presenti.',
      },
    ],
    systems: [
      {
        id: 'daily-goals',
        icon: 'calendar',
        title: 'Obiettivi quotidiani, non intenzioni vaghe',
        detail:
          'Ogni mattina azioni concrete per workout, meal, recovery e abitudini, costruite sul tuo profilo.',
      },
      {
        id: 'adapts',
        icon: 'heart',
        title: 'Si adatta quando le cose cambiano',
        detail:
          'Puoi ripianificare, scambiare o chiedere all\'AI coach. Il piano resta utile invece di diventare vecchio.',
      },
      {
        id: 'verified-progress',
        icon: 'shield',
        title: 'Progressi che puoi verificare',
        detail:
          'Check-in workout, foto meal, note vocali e HealthKit. Niente più auto-reporting basato solo sulla fiducia.',
      },
    ],
  },
  walkthrough: {
    kicker: 'Come funziona',
    title: 'Guarda come scorre una giornata reale.',
    previousStep: 'Step precedente',
    nextStep: 'Step successivo',
    stepsAria: 'Step della guida',
    goToStep: 'Vai allo step {step}',
  },
  features: {
    kicker: 'Sistemi core',
    title: 'Tutto funziona in un unico loop.',
    body:
      'Pianificazione, esecuzione, recovery e coaching in un solo sistema, così il prossimo step non resta mai una decisione da prendere da solo.',
    systems: [
      {
        id: 'daily-execution',
        icon: 'target',
        title: 'Esecuzione quotidiana',
        detail:
          'Obiettivi giornalieri con esercizi, serie, ripetizioni, meal e timer integrato. L\'attenzione resta su cosa fare adesso.',
        active: true,
      },
      {
        id: 'recovery-planning',
        icon: 'heart',
        title: 'Pianificazione del recovery',
        detail:
          'Ripianifica o scambia quando la giornata cambia. Il piano resiste anche alle settimane imperfette.',
        active: true,
      },
      {
        id: 'progress-pressure',
        icon: 'flame',
        title: 'Pressione sul progresso',
        detail:
          'Classifiche, duel settimanali e streak. Il tuo impegno è visibile, non solo a te.',
        active: true,
      },
      {
        id: 'ai-support',
        icon: 'mic',
        title: 'Supporto AI',
        detail:
          'Il coach conosce il tuo piano, i tuoi obiettivi e i tuoi vincoli. Risposte reali, non frasi motivazionali.',
        active: true,
      },
      {
        id: 'workout-focus',
        icon: 'dumbbell',
        title: 'Focus sul workout',
        detail:
          'Timer e check-in riducono l\'attrito durante le sessioni di allenamento.',
        active: false,
      },
      {
        id: 'health-context',
        icon: 'clock',
        title: 'Contesto salute',
        detail:
          'HealthKit può supportare la verifica di passi, sonno e attività.',
        active: false,
      },
    ],
  },
  waitlist: {
    title: 'Inizia oggi a usare Forza.',
    body: 'Scarica Forza dall’App Store, crea il tuo account e inizia il tuo percorso di longevità.',
    honeypotLabel: 'Azienda',
    fields: {
      email: 'La tua email',
    },
    submit: 'Scarica dall’App Store',
    submitting: 'Invio in corso...',
    success:
      'Richiesta ricevuta. Se verrai approvato, riceverai una mail con le istruzioni di sblocco.',
    fallbackError: 'Impossibile inviare la richiesta in questo momento. Riprova.',
  },
  guideChapters: [
    {
      id: 'start',
      label: 'Inizia qui',
      title: 'Orientati dentro il prodotto',
      description:
        'Queste schermate spiegano cosa prova a fare Forza per te ogni giorno: mostrare lo stato attuale, portare dentro il contesto salute e rendere ovvio il prossimo passo.',
    },
    {
      id: 'execution',
      label: 'Esecuzione quotidiana',
      title: 'Passa dall\'intenzione all\'azione',
      description:
        'Questo capitolo copre le schermate che ti aiutano a vivere la giornata reale: capire un obiettivo, fare check-in e ricevere aiuto quando motivazione o logistica iniziano a cedere.',
    },
    {
      id: 'progress',
      label: 'Tracking e motivazione',
      title: 'Capisci se il sistema sta funzionando',
      description:
        'Forza osserva la costanza nel tempo, poi aggiunge pressione competitiva opzionale quando ti serve un motivo in più per presentarti.',
    },
    {
      id: 'personal',
      label: 'Personalizzazione e supporto',
      title: 'Tieni il sistema allineato con la vita reale',
      description:
        'Il prodotto resta utile solo se puoi mantenerlo allineato a vincoli, preferenze, notifiche e bisogni di supporto mentre la vita cambia.',
    },
  ],
  guideSlides: [
    {
      id: 'player-stats',
      chapterId: 'start',
      title: 'I tuoi progressi a colpo d\'occhio',
      description:
        'Player Stats ti dà la lettura più rapida per capire se la settimana è stabile o sta deragliando. Livelli, streak e metriche chiave danno contesto prima della prossima scelta.',
      imageSrc: '/about/player-stats.png',
    },
    {
      id: 'healthkit-toggle',
      chapterId: 'start',
      title: 'Integrazione HealthKit',
      description:
        'Collega i dati salute dell\'iPhone così alcuni obiettivi possono essere verificati automaticamente. Passi, sonno e attività diventano segnali utili, non lavoro manuale extra.',
      imageSrc: '/about/healthkit-toggle.png',
    },
    {
      id: 'daily-quests',
      chapterId: 'start',
      title: 'I tuoi obiettivi quotidiani',
      description:
        'Ogni giorno parte con un set concreto di obiettivi costruiti sul tuo profilo, sulle preferenze e sullo stato di recovery. Il prodotto prova a togliere ambiguità prima che inizi la giornata.',
      imageSrc: '/about/daily-quests.png',
    },
    {
      id: 'quest-detail',
      chapterId: 'start',
      title: 'Dettagli obiettivo',
      description:
        'Ogni obiettivo spiega cosa fare, come completarlo e cosa significa riuscirci. Qui il piano smette di essere astratto e diventa eseguibile.',
      imageSrc: '/about/quest-detail.png',
    },
    {
      id: 'meal-verify',
      chapterId: 'execution',
      title: 'Check-in meal',
      description:
        'I check-in meal collegano pianificazione e comportamento alimentare senza trasformare tutto in logging ossessivo. Il punto è il feedback sull\'aderenza, non attrito inutile.',
      imageSrc: '/about/meal-verify.png',
    },
    {
      id: 'workout-verify',
      chapterId: 'execution',
      title: 'Check-in workout e mindfulness',
      description:
        'Questi check-in fanno riflettere i progressi sul lavoro davvero completato. Il sistema impara meglio quando sa cosa è successo sul serio invece di dare il piano per eseguito.',
      imageSrc: '/about/workout-verify.png',
    },
    {
      id: 'timer',
      chapterId: 'execution',
      title: 'Timer workout',
      description:
        'Il timer integrato riduce l\'attrito durante l\'allenamento. Intervalli, recuperi e transizioni sono gestiti in un solo punto così puoi concentrarti sul movimento.',
      imageSrc: '/about/workout-timer.png',
    },
    {
      id: 'open-mic-coach',
      chapterId: 'execution',
      title: 'Coaching rapido',
      description:
        'Il coaching rapido serve per domande veloci, piccoli aggiustamenti e momenti in cui navigare tra i menu è già troppo attrito. Chiedi, ricevi la risposta, vai avanti.',
      imageSrc: '/about/open-mic-coach.png',
    },
    {
      id: 'live-coach',
      chapterId: 'execution',
      title: 'Coaching live',
      description:
        'Il coaching live è dove Forza diventa più utile nei giorni difficili. Quando energia, orari o compromessi si complicano, l\'app può aiutarti a decidere cosa succede dopo.',
      imageSrc: '/about/live-coach.png',
    },
    {
      id: 'tracker',
      chapterId: 'progress',
      title: 'Tracker dei progressi',
      description:
        'Il tracker mostra i pattern nel tempo, non solo vittorie isolate. La costanza diventa visibile solo quando il prodotto ha una memoria più lunga della giornata corrente.',
      imageSrc: '/about/tracker.png',
    },
    {
      id: 'leaderboard',
      chapterId: 'progress',
      title: 'Classifica',
      description:
        'La classifica aggiunge pressione sociale visibile per chi risponde bene alla competizione. Non è il cuore del prodotto, ma può essere un livello di rinforzo utile.',
      imageSrc: '/about/leaderboard.png',
    },
    {
      id: 'duels',
      chapterId: 'progress',
      title: 'Duel',
      description:
        'I duel trasformano la costanza in un confronto testa a testa. Sono più utili quando una settimana ha bisogno di stakes extra per tenere onesto il follow-through.',
      imageSrc: '/about/duels.png',
    },
    {
      id: 'settings-overview',
      chapterId: 'personal',
      title: 'Impostazioni',
      description:
        'Le impostazioni centralizzano account, privacy, controlli del prodotto e supporto, così il sistema resta comprensibile mentre cresce.',
      imageSrc: '/about/settings-overview.png',
    },
    {
      id: 'manage-plan',
      chapterId: 'personal',
      title: 'Gestisci il tuo piano',
      description:
        'Puoi modificare programma di allenamento, bisogni di recovery e impostazioni più ampie senza ricominciare da zero il rapporto con il prodotto.',
      imageSrc: '/about/manage-plan.png',
    },
    {
      id: 'rescheduling-profile',
      chapterId: 'personal',
      title: 'Ripianificazione e profilo',
      description:
        'Quando la giornata cambia, la ripianificazione aiuta il piano a restare vivo invece di diventare irrilevante. Gli aggiornamenti profilo mantengono il sistema allineato alla disponibilità che evolve.',
      imageSrc: '/about/rescheduling-profile.png',
    },
    {
      id: 'training-preferences',
      chapterId: 'personal',
      title: 'Preferenze di allenamento',
      description:
        'Accesso all\'attrezzatura, gusto di allenamento e vincoli pratici contano. Dati migliori sulle preferenze producono obiettivi quotidiani realistici invece che aspirazionali.',
      imageSrc: '/about/training-preferences.png',
    },
    {
      id: 'notifications-guide',
      chapterId: 'personal',
      title: 'Notifiche e guida funzioni',
      description:
        'Le notifiche tengono vivo il loop durante la giornata, mentre la guida funzioni dà agli utenti un posto dove reimparare il prodotto quando qualcosa non è chiaro.',
      imageSrc: '/about/notifications-guide.png',
    },
    {
      id: 'support',
      chapterId: 'personal',
      title: 'Supporto',
      description:
        'I prodotti early-stage restano affidabili solo se il supporto è facile da raggiungere. Questo flusso tiene l\'aiuto reale dentro il prodotto invece di costringerti a cercarlo altrove.',
      imageSrc: '/about/support.png',
    },
  ],
};

const ES_ES = {
  hero: {
    title: 'La longevidad es una práctica, no un plan',
    body:
      'Objetivos diarios guiados por AI para workouts, meals, recovery y hábitos. Construidos desde tu perfil, adaptados cuando tu día cambia.',
    cta: 'Descarga Forza',
    phoneStageAria: 'Vista previa de la pantalla principal de Forza',
    phoneImageAlt: 'Pantalla principal de Forza',
  },
  why: {
    kicker: 'Por qué Forza',
    title: 'Tu plan debería sobrevivir a tu día',
    points: [
      {
        id: 'static-plan',
        lead: 'El plan era estático.',
        body: 'Tu día cambiaba pero tu plan no. Una sesión perdida se convertía en una semana perdida.',
      },
      {
        id: 'compounding-decisions',
        lead: 'Las decisiones se acumulan.',
        body:
          'Cuando baja la motivación, la siguiente acción tiene que seguir siendo obvia, no quedar escondida en un tracker que ya no miras.',
      },
      {
        id: 'system-not-schedule',
        lead: 'Necesitas un sistema, no un horario.',
        body:
          'Un sistema que te encuentre cada día donde estás de verdad, en lugar de limitarse a esperar que aparezcas.',
      },
    ],
    systems: [
      {
        id: 'daily-goals',
        icon: 'calendar',
        title: 'Objetivos diarios, no intención vaga',
        detail:
          'Cada mañana, acciones concretas para workouts, meals, recovery y hábitos, construidas desde tu perfil.',
      },
      {
        id: 'adapts',
        icon: 'heart',
        title: 'Se adapta cuando las cosas cambian',
        detail:
          'Puedes reprogramar, intercambiar o pedir ayuda al AI coach. Tu plan sigue siendo útil en lugar de quedarse viejo.',
      },
      {
        id: 'verified-progress',
        icon: 'shield',
        title: 'Progreso que puedes verificar',
        detail:
          'Check-ins de workout, fotos de meals, notas de voz y HealthKit. Ya no dependes solo de auto-reportes de confianza.',
      },
    ],
  },
  walkthrough: {
    kicker: 'Cómo funciona',
    title: 'Mira cómo fluye un día real.',
    previousStep: 'Paso anterior',
    nextStep: 'Siguiente paso',
    stepsAria: 'Pasos de la guía',
    goToStep: 'Ir al paso {step}',
  },
  features: {
    kicker: 'Sistemas core',
    title: 'Todo funciona en un solo loop.',
    body:
      'Planificación, ejecución, recovery y coaching en un único sistema, para que el siguiente paso nunca sea una decisión que tengas que tomar solo.',
    systems: [
      {
        id: 'daily-execution',
        icon: 'target',
        title: 'Ejecución diaria',
        detail:
          'Objetivos diarios con ejercicios, series, repeticiones, meals y un temporizador integrado. La atención se queda en lo que toca hacer ahora.',
        active: true,
      },
      {
        id: 'recovery-planning',
        icon: 'heart',
        title: 'Planificación del recovery',
        detail:
          'Reprograma o intercambia cuando tu día cambia. El plan sobrevive incluso a semanas imperfectas.',
        active: true,
      },
      {
        id: 'progress-pressure',
        icon: 'flame',
        title: 'Presión sobre el progreso',
        detail:
          'Leaderboards, duels semanales y streaks. Tu esfuerzo es visible, no solo para ti.',
        active: true,
      },
      {
        id: 'ai-support',
        icon: 'mic',
        title: 'Soporte AI',
        detail:
          'El coach conoce tu plan, tus objetivos y tus restricciones. Respuestas reales, no frases motivacionales.',
        active: true,
      },
      {
        id: 'workout-focus',
        icon: 'dumbbell',
        title: 'Foco en el workout',
        detail:
          'Temporizadores y check-ins reducen la fricción durante las sesiones de entrenamiento.',
        active: false,
      },
      {
        id: 'health-context',
        icon: 'clock',
        title: 'Contexto de salud',
        detail:
          'HealthKit puede apoyar la verificación de pasos, sueño y actividad.',
        active: false,
      },
    ],
  },
  waitlist: {
    title: 'Empieza a usar Forza hoy.',
    body: 'Descarga Forza desde el App Store, crea tu cuenta y empieza tu camino hacia una vida más longeva.',
    honeypotLabel: 'Empresa',
    fields: {
      email: 'Tu correo',
    },
    submit: 'Descargar en el App Store',
    submitting: 'Enviando...',
    success:
      'Solicitud recibida. Si te aprueban, recibirás un correo con instrucciones de desbloqueo.',
    fallbackError: 'No se puede enviar tu solicitud ahora mismo. Inténtalo de nuevo.',
  },
  guideChapters: [
    {
      id: 'start',
      label: 'Empieza aquí',
      title: 'Oriéntate dentro del producto',
      description:
        'Estas pantallas explican qué intenta hacer Forza por ti cada día: mostrar el estado actual, traer contexto de salud y dejar claro el siguiente paso.',
    },
    {
      id: 'execution',
      label: 'Ejecución diaria',
      title: 'Pasa de la intención a la acción',
      description:
        'Este capítulo cubre las pantallas que te ayudan a completar el día real: entender un objetivo, hacer check-in y recibir ayuda cuando la motivación o la logística empiezan a fallar.',
    },
    {
      id: 'progress',
      label: 'Tracking y motivación',
      title: 'Comprueba si el sistema funciona',
      description:
        'Forza sigue la constancia a lo largo del tiempo y luego añade presión competitiva opcional cuando necesitas una razón extra para aparecer.',
    },
    {
      id: 'personal',
      label: 'Personalización y soporte',
      title: 'Mantén el sistema alineado con tu vida real',
      description:
        'El producto solo sigue siendo útil si puedes mantenerlo alineado con restricciones, preferencias, notificaciones y necesidades de soporte mientras la vida cambia.',
    },
  ],
  guideSlides: [
    {
      id: 'player-stats',
      chapterId: 'start',
      title: 'Tu progreso de un vistazo',
      description:
        'Player Stats te da la lectura más rápida para saber si tu semana está estable o se está desviando. Niveles, streaks y métricas clave dan contexto antes de decidir qué hacer después.',
      imageSrc: '/about/player-stats.png',
    },
    {
      id: 'healthkit-toggle',
      chapterId: 'start',
      title: 'Integración con HealthKit',
      description:
        'Conecta los datos de salud del iPhone para que ciertos objetivos puedan verificarse automáticamente. Pasos, sueño y actividad pasan a ser señales útiles, no trabajo manual extra.',
      imageSrc: '/about/healthkit-toggle.png',
    },
    {
      id: 'daily-quests',
      chapterId: 'start',
      title: 'Tus objetivos diarios',
      description:
        'Cada día empieza con un conjunto concreto de objetivos moldeados por tu perfil, tus preferencias y tu estado de recovery. El producto intenta eliminar la ambigüedad antes de que empiece el día.',
      imageSrc: '/about/daily-quests.png',
    },
    {
      id: 'quest-detail',
      chapterId: 'start',
      title: 'Detalles del objetivo',
      description:
        'Cada objetivo explica qué hacer, cómo completarlo y cómo se ve el éxito. Aquí el plan deja de ser abstracto y se vuelve ejecutable.',
      imageSrc: '/about/quest-detail.png',
    },
    {
      id: 'meal-verify',
      chapterId: 'execution',
      title: 'Check-ins de meals',
      description:
        'Los check-ins de meals conectan planificación y comportamiento alimentario sin convertir la experiencia en un registro obsesivo. El punto es el feedback de adherencia, no la fricción porque sí.',
      imageSrc: '/about/meal-verify.png',
    },
    {
      id: 'workout-verify',
      chapterId: 'execution',
      title: 'Check-ins de workout y mindfulness',
      description:
        'Estos check-ins hacen que el progreso refleje el esfuerzo realmente completado. El sistema aprende mejor cuando sabe lo que pasó de verdad en lugar de asumir que el plan se siguió.',
      imageSrc: '/about/workout-verify.png',
    },
    {
      id: 'timer',
      chapterId: 'execution',
      title: 'Temporizador de workout',
      description:
        'El temporizador integrado reduce la fricción durante el entrenamiento. Intervalos, descansos y transiciones se manejan en un solo sitio para que puedas centrarte en moverte.',
      imageSrc: '/about/workout-timer.png',
    },
    {
      id: 'open-mic-coach',
      chapterId: 'execution',
      title: 'Coaching rápido',
      description:
        'El coaching rápido sirve para preguntas veloces, pequeños empujes y momentos en los que navegar por menús ya es demasiada fricción. Pregunta, recibe la respuesta y sigue.',
      imageSrc: '/about/open-mic-coach.png',
    },
    {
      id: 'live-coach',
      chapterId: 'execution',
      title: 'Coaching en vivo',
      description:
        'El coaching en vivo es donde Forza se vuelve más útil en los días difíciles. Cuando baja la energía, cambian los horarios o se complican los compromisos, la app puede ayudarte a decidir qué pasa después.',
      imageSrc: '/about/live-coach.png',
    },
    {
      id: 'tracker',
      chapterId: 'progress',
      title: 'Tracker de progreso',
      description:
        'El tracker muestra patrones a lo largo del tiempo, no solo victorias aisladas. La constancia solo se vuelve visible cuando el producto guarda una memoria más larga que el día actual.',
      imageSrc: '/about/tracker.png',
    },
    {
      id: 'leaderboard',
      chapterId: 'progress',
      title: 'Leaderboard',
      description:
        'El leaderboard añade presión social visible para quienes responden bien a la competición. No es el núcleo del producto, pero puede ser una capa de refuerzo útil.',
      imageSrc: '/about/leaderboard.png',
    },
    {
      id: 'duels',
      chapterId: 'progress',
      title: 'Duels',
      description:
        'Los duels convierten la constancia en una competición cara a cara. Son más útiles cuando una semana necesita stakes extra para mantener honesto tu follow-through.',
      imageSrc: '/about/duels.png',
    },
    {
      id: 'settings-overview',
      chapterId: 'personal',
      title: 'Ajustes',
      description:
        'Los ajustes centralizan cuenta, privacidad, controles del producto y soporte para que el sistema siga siendo comprensible mientras crece.',
      imageSrc: '/about/settings-overview.png',
    },
    {
      id: 'manage-plan',
      chapterId: 'personal',
      title: 'Gestiona tu plan',
      description:
        'Puedes ajustar el calendario de entrenamiento, las necesidades de recovery y la configuración amplia del plan sin reiniciar tu relación con el producto desde cero.',
      imageSrc: '/about/manage-plan.png',
    },
    {
      id: 'rescheduling-profile',
      chapterId: 'personal',
      title: 'Reprogramación y perfil',
      description:
        'Cuando tu día cambia, reprogramar ayuda a que el plan siga vivo en lugar de volverse irrelevante. Las actualizaciones del perfil mantienen el sistema alineado con una disponibilidad que evoluciona.',
      imageSrc: '/about/rescheduling-profile.png',
    },
    {
      id: 'training-preferences',
      chapterId: 'personal',
      title: 'Preferencias de entrenamiento',
      description:
        'El acceso al equipo, el gusto de entrenamiento y las restricciones prácticas importan. Unos mejores datos de preferencias producen objetivos diarios realistas en lugar de aspiracionales.',
      imageSrc: '/about/training-preferences.png',
    },
    {
      id: 'notifications-guide',
      chapterId: 'personal',
      title: 'Notificaciones y guía de funciones',
      description:
        'Las notificaciones mantienen activo el loop durante el día, mientras la guía de funciones da a los usuarios un lugar donde reaprender el producto cuando algo no está claro.',
      imageSrc: '/about/notifications-guide.png',
    },
    {
      id: 'support',
      chapterId: 'personal',
      title: 'Soporte',
      description:
        'Los productos en fase temprana solo siguen siendo fiables si el soporte es fácil de alcanzar. Este flujo mantiene la ayuda real dentro del producto en lugar de obligarte a buscarla fuera.',
      imageSrc: '/about/support.png',
    },
  ],
};

const CONTENT = Object.freeze({
  'en-US': EN_US,
  'it-IT': IT_IT,
  'es-ES': ES_ES,
});

export const getAboutForzaContent = (locale) => CONTENT[normalizeLocale(locale)] || EN_US;

export const ABOUT_FORZA_GUIDE_CHAPTERS = EN_US.guideChapters;
export const ABOUT_FORZA_GUIDE_SLIDES = EN_US.guideSlides;
